/**
 * Fleet headline for 系统健康透视.
 *
 * Leads with the blended margin, because "is this window profitable" is the
 * one question no other ops page answers on the same canvas as health.
 *
 * ── The unsettled rule ──
 *
 * When `summary.money.settled` is false the margin card renders 未结算 with
 * the explanation attached, never 0%. The ledger is written by the rating
 * pipeline, which settles behind live traffic, so a short window routinely
 * has requests and no rated rows yet. A 0% there would be a claim — "we sold
 * this at cost" — and it would be false.
 *
 * ── Grades and their denominator ──
 *
 * The fleet grade rides next to the request count on purpose, with the
 * fraction it was computed from — attributable failures over attributable
 * requests — beside it. The shared error rule's critical arm has NO
 * minimum-sample floor (only the warning arm does,
 * `components/trafficHealth.ts`), which is the gateway's own rule and is not
 * softened here; showing the denominator is what lets an operator tell an
 * outage from a three-request blip.
 *
 * ── Three error cards, three subjects ──
 *
 * They are not a total and two subsets, and no two of them may be compared
 * without saying which population each is over (`types.ts`):
 *
 *   下游失败率  `downstreamErrorRate`  ours to answer for. Carries a tone.
 *   上游 5xx    `serverErrorRate`      supply's. Carries a tone. Includes the
 *                                      requests the ladder rescued, which the
 *                                      downstream card counts as successes.
 *   客户侧失败  `errorRate`            what tenants saw, refusals included.
 *                                      NO tone: on 2026-09-01 the fleet ran
 *                                      15.7% here against 0.2% 5xx, nearly
 *                                      all of it one tenant's SDK being
 *                                      refused at the gate. A red 15.7%
 *                                      would have announced an incident that
 *                                      was not ours — and dropping the card
 *                                      would have hidden a paying customer
 *                                      failing three quarters of their calls.
 */

import { useT } from '../../i18n/index.ts';
import { formatCnyCents } from '../../components/formatters.ts';
import {
  downstreamSeverity,
  fallbackSeverity,
  slowFirstByteSeverity,
  supplySeverity,
} from '../../components/trafficHealth.ts';
import { InfoHint, KpiCard } from '../../ui/index.ts';
import {
  GRADE_LABEL_KEY,
  GRADE_LAMP_TONE,
  formatDurationMs,
  formatInteger,
  formatMaybeInteger,
  formatRate,
  severityKpiTone,
  severityLamp,
} from './display.ts';
import { supplyDialsOf, supplyFailuresOf, supplyRateOf } from './model-nodes.ts';
import type { FleetSummary } from './types.ts';

interface Props {
  readonly summary: FleetSummary;
}

export function SummaryStrip({ summary }: Props): JSX.Element {
  const t = useT();
  const traffic = summary.traffic;
  const money = summary.money;
  // Unknown when the refusal counter is absent: the fleet card must not be
  // toned off a substituted zero. `null` tone reads as no claim.
  const downstreamKnown =
    traffic.downstreamErrorRate !== null && traffic.attributableRequests !== null;
  const downstreamTone = downstreamKnown
    ? downstreamSeverity(traffic.downstreamErrorRate ?? 0, traffic.attributableRequests ?? 0)
    : null;
  // The #2240 figure over dials where the gateway reports it, else the 5xx
  // floor — the same choice every other supply cell on the page makes.
  const supplyTone = supplySeverity(supplyRateOf(traffic), supplyDialsOf(traffic));
  const fallbackTone = fallbackSeverity(traffic.fallbackRate);
  const firstByteTone = slowFirstByteSeverity(traffic.slowFirstByteRate);

  return (
    <div
      className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5"
      data-testid="health-perspective-summary"
    >
      <MarginCard money={money} />
      <KpiCard
        testId="health-perspective-kpi-requests"
        label={t('health_perspective.summary.requests')}
        value={formatInteger(traffic.requests)}
        lamp={GRADE_LAMP_TONE[summary.grade]}
        delta={{
          // Grade and denominator together — see the module header.
          text: `${t(GRADE_LABEL_KEY[summary.grade])} · ${t('health_perspective.table.denominator', {
            errors: formatMaybeInteger(traffic.attributableErrors),
            requests: formatMaybeInteger(traffic.attributableRequests),
          })}`,
          tone: 'neutral',
        }}
      />
      {/* The supply verdict. The only error card that carries a tone. */}
      <KpiCard
        testId="health-perspective-kpi-supply-error-rate"
        label={t('health_perspective.summary.supply_error_rate')}
        value={formatRate(supplyRateOf(traffic))}
        tone={severityKpiTone(supplyTone)}
        lamp={severityLamp(supplyTone)}
        delta={{
          text: `${formatInteger(supplyFailuresOf(traffic))} / ${formatInteger(supplyDialsOf(traffic))}`,
          tone: 'neutral',
        }}
      />
      {/* THE downstream verdict: failures on the requests we took
          responsibility for. Refused-before-upstream requests leave both
          sides of the fraction, so our own gate doing its job cannot show up
          here as our outage. */}
      <KpiCard
        testId="health-perspective-kpi-downstream-error-rate"
        label={t('health_perspective.summary.downstream_error_rate')}
        value={formatRate(traffic.downstreamErrorRate)}
        tone={downstreamTone === null ? 'default' : severityKpiTone(downstreamTone)}
        lamp={downstreamTone === null ? undefined : severityLamp(downstreamTone)}
        delta={{
          text: downstreamKnown
            ? `${formatMaybeInteger(traffic.attributableErrors)} / ${formatMaybeInteger(traffic.attributableRequests)}`
            : t('health_perspective.refusal.unknown_short'),
          tone: 'neutral',
        }}
      />
      {/* The tenant lens — what customers finally saw, refusals included.
          Deliberately tone-less: it is a fact about customers, not a verdict
          about us, and a red 76% here would announce an incident that is not
          ours. The hint says so. */}
      <KpiCard
        testId="health-perspective-kpi-error-rate"
        label={
          <span className="inline-flex items-center gap-1.5">
            {t('health_perspective.summary.tenant_error_rate')}
            <InfoHint
              label={t('health_perspective.summary.tenant_error_rate')}
              content={t('health_perspective.summary.tenant_error_hint')}
              testId="health-perspective-tenant-error-hint"
            />
          </span>
        }
        value={formatRate(traffic.errorRate)}
        delta={{
          text: t('health_perspective.bar.client', {
            rate: formatRate(traffic.errorRate),
            rateLimited: formatInteger(traffic.rateLimited),
            upstreamRateLimited: formatMaybeInteger(traffic.upstreamAttemptRateLimited),
          }),
          tone: 'neutral',
        }}
      />
      <KpiCard
        testId="health-perspective-kpi-fallback-rate"
        label={t('health_perspective.summary.fallback_rate')}
        value={formatRate(traffic.fallbackRate)}
        tone={severityKpiTone(fallbackTone)}
        lamp={severityLamp(fallbackTone)}
        // Against 5xx, not against requests: a fallback only ever had the
        // chance to catch a request that actually reached an upstream.
        delta={{
          text: t('health_perspective.table.cell.supply_detail', {
            errors: formatInteger(supplyFailuresOf(traffic)),
            fallbacks: formatInteger(traffic.fallbacks),
          }),
          tone: 'neutral',
        }}
      />
      <KpiCard
        testId="health-perspective-kpi-first-byte"
        label={t('health_perspective.summary.first_byte')}
        value={formatDurationMs(traffic.avgFirstByteMs)}
        tone={severityKpiTone(firstByteTone)}
        lamp={severityLamp(firstByteTone)}
        delta={{ text: formatRate(traffic.slowFirstByteRate), tone: 'neutral' }}
      />
      {/* Denominator is `pricedSkuCount`, the population the economics slice
          actually counted over — not `skuCount`, which is this page's wider
          union and includes the unresolved-SKU bucket. Undefined rather than
          a fallback when the slice degraded: no denominator beats a wrong
          one. */}
      <CoverageCard
        testId="health-perspective-kpi-unrouted"
        label={t('health_perspective.summary.unrouted')}
        count={summary.unroutedSkuCount}
        total={summary.pricedSkuCount ?? undefined}
        tone="warn"
      />
      <CoverageCard
        testId="health-perspective-kpi-single-point"
        label={t('health_perspective.summary.single_point')}
        count={summary.singlePointSkuCount}
        total={summary.pricedSkuCount ?? undefined}
        tone="warn"
      />
      {/* No denominator: this counts UPSTREAMS, and the model's supply
          counters are channels and (channel × model) pairs — neither is the
          same population. Putting one of them under this number would be a
          real figure attached to the wrong subject, so the count stands
          alone rather than borrowing a plausible-looking total. */}
      <CoverageCard
        testId="health-perspective-kpi-unhealthy-upstreams"
        label={t('health_perspective.summary.unhealthy_upstreams')}
        count={summary.unhealthyUpstreamCount}
        tone="bad"
      />
    </div>
  );
}

function MarginCard({ money }: { readonly money: FleetSummary['money'] }): JSX.Element {
  const t = useT();
  const label = (
    <span className="inline-flex items-center gap-1.5">
      {t('health_perspective.summary.margin')}
      {money.settled ? null : (
        <InfoHint
          label={t('health_perspective.summary.unsettled')}
          content={t('health_perspective.summary.unsettled_hint')}
          testId="health-perspective-unsettled-hint"
        />
      )}
    </span>
  );

  if (!money.settled) {
    return (
      <KpiCard
        testId="health-perspective-kpi-margin"
        label={label}
        value={t('health_perspective.summary.unsettled')}
        delta={{ text: `${t('health_perspective.node.requests')} ${formatInteger(money.usageCount)}`, tone: 'neutral' }}
      />
    );
  }

  const revenue = money.revenueCnyCents;
  const cost = money.upstreamCostCnyCents;
  return (
    <KpiCard
      testId="health-perspective-kpi-margin"
      label={label}
      value={formatRate(money.marginPct)}
      // A negative blended margin is a fact, not a threshold: the window
      // sold below procurement cost. Everything else stays neutral rather
      // than inventing a "good margin" line this page has no mandate to draw.
      tone={money.marginPct !== null && money.marginPct < 0 ? 'bad' : 'default'}
      delta={{
        text: `${revenue === null ? '—' : formatCnyCents(revenue, false)} − ${cost === null ? '—' : formatCnyCents(cost, false)}`,
        tone: 'neutral',
      }}
    />
  );
}

/**
 * A coverage counter. `null` means the slice that would have counted it
 * failed, and renders as an em dash with the unknown grade spelled out —
 * never as 0, which would read as "nothing is wrong".
 *
 * `total` is optional because not every counter has a population the model
 * measures. An absent one renders no delta line at all, which is the honest
 * answer; substituting a different population's size would be worse than
 * saying nothing.
 */
function CoverageCard({
  testId,
  label,
  count,
  total,
  tone,
}: {
  readonly testId: string;
  readonly label: string;
  readonly count: number | null;
  readonly total?: number;
  readonly tone: 'warn' | 'bad';
}): JSX.Element {
  const t = useT();
  if (count === null) {
    return (
      <KpiCard
        testId={testId}
        label={label}
        value="—"
        delta={{ text: t('health_perspective.grade.unknown'), tone: 'neutral' }}
      />
    );
  }
  return (
    <KpiCard
      testId={testId}
      label={label}
      value={formatInteger(count)}
      tone={count > 0 ? tone : 'default'}
      lamp={count > 0 ? tone : undefined}
      delta={total === undefined ? undefined : { text: `/ ${formatInteger(total)}`, tone: 'neutral' }}
    />
  );
}
